import { useEffect, useRef, useState } from 'react'

/**
 * The top padding of the nearest scrolling ancestor.
 *
 * `position: sticky` offsets are measured from the scrollport's *padding* box, so a bar with
 * `top: 0` inside the app shell pins 24–40px below the viewport edge (`<main>` carries
 * `py-6 sm:py-8 md:py-10`) and content scrolls through the strip above it. Painting over that
 * strip unconditionally breaks the unscrolled state, and CSS has no "is stuck" selector, so
 * the offset has to be measured and applied as a negative `top`.
 */
export const useScrollportPadding = () => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [paddingTop, setPaddingTop] = useState(0)

  useEffect(() => {
    const scrollportEl = anchorRef.current?.closest('main')
    if (!scrollportEl) return undefined

    const measure = () =>
      setPaddingTop(
        Number.parseFloat(
          window.getComputedStyle(scrollportEl).paddingTop || '0'
        )
      )
    measure()

    // The padding changes at the sm/md breakpoints, so it has to be re-read on resize.
    const observer = new ResizeObserver(measure)
    observer.observe(scrollportEl)

    return () => {
      observer.disconnect()
    }
  }, [])

  return { anchorRef, paddingTop }
}
